import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  IconButton,
  Typography,
  CircularProgress,
  Grid,
  Alert,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
const SaleAdd = ({ open, onClose, token }) => {
  const GENERAL_CUSTOMER = { id: "general-customer", name: "General Customer" };
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd("shop/sales", token);

  // Fetch dropdown data for shops, customers, units, and currencies.
  const { data: shopsData, loading: shopLoading } = useFetchObjects(
    ["shop", "nopagination", "v2"],
    "shop/shop/?page_size=all",
    token
  );
  const { data: customers, loading: customerLoading } = useFetchObjects(
    ["customer", "nopagination", "v2"],
    "shop/customers/?page_size=all",
    token
  );
  const { data: units, loading: unitLoading } = useFetchObjects(
    ["unit", "nopagination", "v2"],
    "shop/units/?page_size=all",
    token
  );
  const unitsData = units;
  const { data: currencies, loading: currencyLoading } = useFetchObjects(
    ["currency", "nopagination", "v2"],
    "shop/currencies/?page_size=all",
    token
  );
  const { data: exchangeRatesData } = useFetchObjects(
    ["sale-add-exchange-rates"],
    "shop/exchange-rates/?page_size=1000",
    token
  );
  const { data: employeesData, loading: employeeLoading } = useFetchObjects(
    ["employees", "sale", "add"],
    "shop/employees/?page_size=all",
    token
  );

  // Fetch all stocks (no shop filter) so all purchase items are always available.
  const [shop, setShop] = useState("");
  const [stocks, setStocks] = useState([]);
  const {
    data: stocksData,
    loading: stockLoading,
    refetch: refetchStocks,
  } = useFetchObjects(
    ["stock", "sale", "nopagination", "v2"],
    "shop/stocks/?page_size=all",
    token
  );

  useEffect(() => {
    if (stocksData) {
      const stocksList = Array.isArray(stocksData) ? stocksData : (stocksData?.results || []);
      setStocks(stocksList);
    } else {
      setStocks([]);
    }
  }, [stocksData]);

  useEffect(() => {
    if (open) {
      refetchStocks();
    }
  }, [open, refetchStocks]);

  const shopsList = Array.isArray(shopsData) ? shopsData : (shopsData?.results || []);
  const hasSingleShop = shopsList.length === 1;
  const customersList = Array.isArray(customers) ? customers : (customers?.results || []);
  const unitsList = Array.isArray(units) ? units : (units?.results || []);
  const allCurrenciesList = useMemo(
    () => (Array.isArray(currencies) ? currencies : (currencies?.results || [])),
    [currencies]
  );
  const currenciesList = useMemo(
    () =>
      shop
        ? allCurrenciesList.filter((currency) => String(currency?.shop) === String(shop))
        : allCurrenciesList,
    [allCurrenciesList, shop]
  );
  const employeesList = Array.isArray(employeesData)
    ? employeesData
    : (employeesData?.results || []);

  console.log('Shop selected:', shop);
  console.log('Customers data:', customers);
  console.log('Customers list length:', customersList.length);

  // Auto-select shop if only one exists.
  useEffect(() => {
    if (shopsList.length === 1 && !shop) {
      setShop(String(shopsList[0].id));
    }
  }, [shopsList, shop]);

  const getStockShopId = (stock) => String(stock?.shop || stock?.shop_details?.id || "");
  const getStockQuantity = (stock) => Number(stock?.quantity || stock?.availableQty || 0) || 0;
  // Prefer product id so different products that share a code still appear in sale.
  const getProductDedupeKey = (product) => {
    if (product?.id != null && product.id !== "") return `id:${product.id}`;
    const code = String(product?.code || product?.product_code || "").trim().toUpperCase();
    if (code) return `code:${code}`;
    return `name:${String(product?.name || "").trim().replace(/\s+/g, " ").toUpperCase()}`;
  };

  // Derive unique products from stocks using purchase_item_details.
  // If a shop is selected, only show products available in that shop.
  const uniqueProducts = useMemo(() => {
    const stocksList = Array.isArray(stocks) ? stocks : [];
    const filteredStocks = stocksList.filter((s) => {
      if (getStockQuantity(s) <= 0) return false;
      return !shop || getStockShopId(s) === String(shop);
    });
    const prodMap = new Map();
    filteredStocks.forEach((stock) => {
      const prod = stock.purchase_item_details?.product;
      if (!prod?.id) return;
      const key = getProductDedupeKey(prod);
      const current = prodMap.get(key);
      if (!current || getStockQuantity(stock) > getStockQuantity(current.stock)) {
        prodMap.set(key, { product: prod, stock });
      }
    });
    return Array.from(prodMap.values()).map((entry) => entry.product);
  }, [stocks, shop]);

  // Local state for sale-level fields.
  const [customerSelected, setCustomerSelected] = useState("");
  const [generalCustomerName, setGeneralCustomerName] = useState("");
  const [generalCustomerPhone, setGeneralCustomerPhone] = useState("");
  const [saleDate, setSaleDate] = useState(""); // date only
  const [saleCurrency, setSaleCurrency] = useState(""); // sale-level currency
  const [discount, setDiscount] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [receivedAmount, setReceivedAmount] = useState("");
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");
  const [notes, setNotes] = useState("");
  const [priceType, setPriceType] = useState("retail"); // "wholesale" | "retail"
  const selectedCurrency = React.useMemo(() => {
    return (
      currenciesList.find((c) => String(c.id) === String(saleCurrency)) ||
      allCurrenciesList.find((c) => String(c.id) === String(saleCurrency)) ||
      null
    );
  }, [currenciesList, allCurrenciesList, saleCurrency]);
  const selectedCurrencyTitle = React.useMemo(() => {
    return (selectedCurrency?.title || "").toUpperCase();
  }, [selectedCurrency]);
  const getCurrencyLabel = React.useCallback((currency) => {
    if (!currency) return "";
    return currency.title;
  }, []);

  const normalizeCurrencyCode = React.useCallback((value) => {
    const code = String(value || "").trim().toUpperCase();
    if (code === "AFN" || code === "AFG" || code === "؋" || code.includes("AFGHAN") || code.includes("افغ")) return "AFG";
    if (code === "USD" || code === "$" || code.includes("DOLLAR") || code.includes("دالر")) return "USD";
    return code;
  }, []);

  const firstPositivePrice = React.useCallback((...values) => {
    const value = values.find((item) => {
      const amount = Number(item);
      return item !== null && item !== undefined && item !== "" && Number.isFinite(amount) && amount > 0;
    });
    return value ?? "";
  }, []);

  const pickPriceByCurrency = React.useCallback((details, baseField, currencyTitle, fallback) => {
    const code = normalizeCurrencyCode(currencyTitle);
    const suffix = code === "USD" ? "_usd" : "_afg";
    const v = details?.[`${baseField}${suffix}`];
    if (v !== null && v !== undefined && v !== "" && Number(v) > 0) return v;
    return fallback;
  }, [normalizeCurrencyCode]);

  const latestRateMap = React.useMemo(() => {
    const rows = exchangeRatesData?.results || exchangeRatesData || [];
    const map = new Map();
    rows.forEach((rate) => {
      const from = normalizeCurrencyCode(rate?.from_currency_title || rate?.from_currency?.title);
      const to = normalizeCurrencyCode(rate?.to_currency_title || rate?.to_currency?.title);
      const value = Number(rate?.rate);
      if (!from || !to || !Number.isFinite(value) || value <= 0) return;
      const key = `${from}->${to}`;
      const prev = map.get(key);
      const rateDate = String(rate?.rate_date || "");
      if (!prev || rateDate > prev.rateDate) {
        map.set(key, { rate: value, rateDate });
      }
    });
    return map;
  }, [exchangeRatesData, normalizeCurrencyCode]);

  const convertPrice = React.useCallback((amount, fromCurrency, toCurrency) => {
    const from = normalizeCurrencyCode(fromCurrency);
    const to = normalizeCurrencyCode(toCurrency);
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return null;
    if (!from || !to || from === to) return numericAmount;
    const direct = latestRateMap.get(`${from}->${to}`);
    if (direct?.rate) return numericAmount * direct.rate;
    const reverse = latestRateMap.get(`${to}->${from}`);
    if (reverse?.rate) return numericAmount / reverse.rate;
    const fallbackRate = Number(exchangeRate);
    if (Number.isFinite(fallbackRate) && fallbackRate > 0) {
      if (from === "AFG" && to === "USD") return numericAmount / fallbackRate;
      if (from === "USD" && to === "AFG") return numericAmount * fallbackRate;
    }
    return null;
  }, [exchangeRate, latestRateMap, normalizeCurrencyCode]);

  const getStockPriceByCurrency = React.useCallback((stockRecord, matchingStocks, baseField, fallback) => {
    const selectedCode = normalizeCurrencyCode(selectedCurrencyTitle);
    const details = stockRecord?.purchase_item_details || {};
    const directValue = pickPriceByCurrency(details, baseField, selectedCode, "");
    if (directValue !== null && directValue !== undefined && directValue !== "" && Number(directValue) > 0) return directValue;

    const matchingDirectValue = (matchingStocks || [])
      .map((stock) => pickPriceByCurrency(stock?.purchase_item_details || {}, baseField, selectedCode, ""))
      .find((value) => value !== null && value !== undefined && value !== "" && Number(value) > 0);
    if (matchingDirectValue !== null && matchingDirectValue !== undefined && matchingDirectValue !== "") {
      return matchingDirectValue;
    }

    const stockWithCurrency = (matchingStocks || []).find(
      (stock) =>
        normalizeCurrencyCode(stock?.currency_details?.title) === selectedCode
    );
    const currencyDetails = stockWithCurrency?.purchase_item_details || {};
    const currencyValue =
      currencyDetails?.[baseField] ??
      (baseField === "retail_price" ? currencyDetails?.sale_price : null);
    if (currencyValue !== null && currencyValue !== undefined && currencyValue !== "" && Number(currencyValue) > 0) {
      return currencyValue;
    }

    const oppositeCode = selectedCode === "USD" ? "AFG" : "USD";
    const oppositeValue = pickPriceByCurrency(details, baseField, oppositeCode, "");
    const convertedOppositeValue = convertPrice(oppositeValue, oppositeCode, selectedCode);
    if (convertedOppositeValue !== null) {
      return convertedOppositeValue.toFixed(2);
    }

    const stockCurrency = normalizeCurrencyCode(stockRecord?.currency_details?.title);
    const baseValue =
      details?.[baseField] ??
      (baseField === "retail_price" ? details?.sale_price || stockRecord?.sale_price : null) ??
      fallback;
    const convertedBaseValue = convertPrice(baseValue, stockCurrency, selectedCode);
    if (convertedBaseValue !== null) {
      return convertedBaseValue.toFixed(2);
    }

    return fallback;
  }, [convertPrice, normalizeCurrencyCode, pickPriceByCurrency, selectedCurrencyTitle]);

  const stockMatchesSelectedCurrency = React.useCallback((stock) => {
    if (!selectedCurrency) return true;
    const stockCurrencyId = stock?.currency_details?.id;
    if (stockCurrencyId != null && String(stockCurrencyId) === String(selectedCurrency.id)) {
      return true;
    }
    return (
      normalizeCurrencyCode(stock?.currency_details?.title) ===
      normalizeCurrencyCode(selectedCurrency.title)
    );
  }, [normalizeCurrencyCode, selectedCurrency]);
  const [items, setItems] = useState([]);
  const [formError, setFormError] = useState("");
  const [errors, setErrors] = useState({});
  const getCustomerShopId = React.useCallback(
    (customer) => customer?.shop ?? customer?.shop_details?.id ?? "",
    []
  );
  const filteredCustomersList = useMemo(
    () =>
      shop
        ? customersList.filter((customer) => String(getCustomerShopId(customer)) === String(shop))
        : customersList,
    [customersList, getCustomerShopId, shop]
  );
  const customerOptions = useMemo(() => [GENERAL_CUSTOMER, ...filteredCustomersList], [filteredCustomersList]);
  const isGeneralCustomerSelected = String(customerSelected) === GENERAL_CUSTOMER.id;
  const selectedCustomerObject = useMemo(
    () =>
      filteredCustomersList.find(
        (customer) => String(customer.id) === String(customerSelected)
      ) || null,
    [customerSelected, filteredCustomersList]
  );
  const selectedCustomerBalance = selectedCustomerObject?.total_owed_by_currency;
  const selectedCustomerBalanceText =
    selectedCustomerBalance && typeof selectedCustomerBalance === "object"
      ? Object.entries(selectedCustomerBalance)
          .map(([cur, amt]) => `${Number(amt).toFixed(2)} ${cur}`)
          .join(", ") || "0"
      : "0";

  useEffect(() => {
    if (!shop || !customerSelected || isGeneralCustomerSelected) return;
    const selectedCustomerStillValid = filteredCustomersList.some(
      (customer) => String(customer.id) === String(customerSelected)
    );
    if (!selectedCustomerStillValid) {
      setCustomerSelected("");
    }
  }, [customerSelected, filteredCustomersList, isGeneralCustomerSelected, shop]);

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "sale-add", shop],
    shop ? `shop/accounts/?page_size=all&shop=${shop}` : "",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];
  const selectedAccount = useMemo(
    () => accounts.find((acc) => String(acc.id) === String(selectedAccountId)) || null,
    [accounts, selectedAccountId]
  );
  const sameCurrencyAccounts = useMemo(
    () => accounts.filter((acc) => String(acc.currency) === String(saleCurrency)),
    [accounts, saleCurrency]
  );

  const { data: directRateData } = useFetchObjects(
    ["sale-add-rate-direct", shop, saleCurrency, selectedAccount?.currency],
    shop && saleCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${shop}&from_currency=${saleCurrency}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    ["sale-add-rate-reverse", shop, saleCurrency, selectedAccount?.currency],
    shop && saleCurrency && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${shop}&from_currency=${selectedAccount.currency}&to_currency=${saleCurrency}`
      : "",
    token
  );

  useEffect(() => {
    if (
      shop &&
      selectedCurrency?.shop &&
      String(selectedCurrency.shop) !== String(shop)
    ) {
      setSaleCurrency("");
      return;
    }

    if (
      saleCurrency &&
      currenciesList.length > 0 &&
      !currenciesList.some((currency) => String(currency.id) === String(saleCurrency))
    ) {
      setSaleCurrency("");
      return;
    }

    if (!shop || !saleCurrency) {
      setSelectedAccountId("");
      return;
    }
    if (accounts.length === 0) return;
    const exists = accounts.some((acc) => String(acc.id) === String(selectedAccountId));
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || accounts[0];
      setSelectedAccountId(preferred ? String(preferred.id) : "");
    }
  }, [shop, saleCurrency, accounts, sameCurrencyAccounts, selectedAccountId, currenciesList, selectedCurrency]);

  // Add a new sale item row.
  const addItem = () => {
    setItems([
      ...items,
      {
        product: "",
        unit: "",
        available: "",
        quantity: "",
        sale_price: "",
        buy_price: "",
        purchase_item: "",
        wholesale_price: "",
        retail_price: "",
      },
    ]);
  };

  // Remove an item row.
  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // When a product is selected from Autocomplete, auto-fill unit, sale_price, and available quantity.
  const handleProductSelect = (index, selectedProduct) => {
    const newItems = [...items];
    newItems[index].product = selectedProduct ? String(selectedProduct.id) : "";
    if (selectedProduct) {
      const matchingStocks = stocks.filter(
        (stock) =>
          stock.purchase_item_details &&
          stock.purchase_item_details.product &&
          String(stock.purchase_item_details.product.id) === String(selectedProduct.id)
      );
      const byShop = (shop
        ? matchingStocks.filter((s) => getStockShopId(s) === String(shop))
        : matchingStocks
      ).filter((s) => getStockQuantity(s) > 0);
      const byCurrency = selectedCurrencyTitle
        ? byShop.filter(
            (s) => stockMatchesSelectedCurrency(s)
          )
        : byShop;
      const stockRecord = byCurrency[0] || byShop[0] || (!shop ? matchingStocks.find((s) => getStockQuantity(s) > 0) : null);
      if (stockRecord) {
        const details = stockRecord.purchase_item_details || {};
        const bp = getStockPriceByCurrency(
          stockRecord,
          matchingStocks,
          "buy_price",
          details?.buy_price || ""
        );
        const wp = getStockPriceByCurrency(
          stockRecord,
          matchingStocks,
          "wholesale_price",
          details?.wholesale_price || ""
        );
        const rp = getStockPriceByCurrency(
          stockRecord,
          matchingStocks,
          "retail_price",
          details?.retail_price || details?.sale_price || stockRecord.sale_price || ""
        );

        newItems[index].unit = stockRecord.unit_details ? String(stockRecord.unit_details.id) : "";
        newItems[index].available = stockRecord.quantity;
        newItems[index].buy_price = bp;
        newItems[index].purchase_item = details?.id || stockRecord.purchase_item || "";
        newItems[index].wholesale_price = wp;
        newItems[index].retail_price = rp;

        // Auto-fill sale_price based on the global priceType selector
        if (priceType === "wholesale") {
          newItems[index].sale_price = firstPositivePrice(wp, rp, stockRecord.sale_price);
        } else {
          // retail (default)
          newItems[index].sale_price = firstPositivePrice(rp, stockRecord.sale_price);
        }
      } else {
        newItems[index].unit = "";
        newItems[index].sale_price = "";
        newItems[index].available = "0";
        newItems[index].buy_price = "";
        newItems[index].purchase_item = "";
        newItems[index].wholesale_price = "";
        newItems[index].retail_price = "";
      }
    } else {
      newItems[index].unit = "";
      newItems[index].sale_price = "";
      newItems[index].available = "0";
      newItems[index].buy_price = "";
      newItems[index].purchase_item = "";
      newItems[index].wholesale_price = "";
      newItems[index].retail_price = "";
    }
    setItems(newItems);
  };

  // When the global price type changes, re-apply to all items that already have a product
  const handlePriceTypeChange = (newType) => {
    setPriceType(newType);
    setItems((prev) =>
      prev.map((item) => {
        if (!item.product) return item;
        if (newType === "wholesale") {
          return { ...item, sale_price: firstPositivePrice(item.wholesale_price, item.retail_price, item.sale_price) };
        }
        // retail
        return { ...item, sale_price: firstPositivePrice(item.retail_price, item.sale_price) };
      })
    );
  };

  useEffect(() => {
    if (!selectedCurrencyTitle) return;
    setItems((prev) =>
      prev.map((item) => {
        if (!item.product) return item;
        const matchingStocks = stocks.filter(
          (stock) =>
            stock.purchase_item_details?.product &&
            String(stock.purchase_item_details.product.id) === String(item.product)
        );
        const branchStocks = matchingStocks.filter(
          (stock) =>
            String(stock.purchase_item_details.product.id) === String(item.product) &&
            (!shop || getStockShopId(stock) === String(shop)) &&
            getStockQuantity(stock) > 0
        );
        const byCurrency = selectedCurrencyTitle
          ? branchStocks.filter(
              (s) => stockMatchesSelectedCurrency(s)
            )
          : branchStocks;
        const stockRecord = byCurrency[0] || branchStocks[0] || (!shop ? matchingStocks.find((s) => getStockQuantity(s) > 0) : null);
        if (!stockRecord) return item;
        const details = stockRecord.purchase_item_details || {};
        const bp = getStockPriceByCurrency(stockRecord, matchingStocks, "buy_price", details?.buy_price || "");
        const wp = getStockPriceByCurrency(stockRecord, matchingStocks, "wholesale_price", details?.wholesale_price || "");
        const rp = getStockPriceByCurrency(stockRecord, matchingStocks, "retail_price", details?.retail_price || details?.sale_price || stockRecord.sale_price || "");
        return {
          ...item,
          buy_price: bp,
          purchase_item: details?.id || stockRecord.purchase_item || item.purchase_item || "",
          wholesale_price: wp,
          retail_price: rp,
          sale_price: priceType === "wholesale"
            ? firstPositivePrice(wp, rp, item.sale_price)
            : firstPositivePrice(rp, item.sale_price),
        };
      })
    );
  }, [selectedCurrencyTitle, priceType, stocks, shop, getStockPriceByCurrency, stockMatchesSelectedCurrency, firstPositivePrice]);

  // Update a sale item field.
  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  // Calculate total sale amount.
  const totalAmount = items.reduce((acc, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const price = parseFloat(item.sale_price) || 0;
    return acc + qty * price;
  }, 0);

  const discountValue = parseFloat(discount) || 0;
  const finalTotal = totalAmount - discountValue;
  const parsedReceived = parseFloat(receivedAmount) || 0;
  const receivedValid = parsedReceived <= finalTotal;
  const needsPaymentAccount = parsedReceived > 0;

  const exchangeRatePreview = useMemo(() => {
    if (!saleCurrency) return "";
    const accountCurrencyId = selectedAccount?.currency ?? saleCurrency;
    if (String(saleCurrency) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [saleCurrency, selectedAccount, directRateData, reverseRateData]);

  useEffect(() => {
    setExchangeRate(exchangeRatePreview);
  }, [exchangeRatePreview]);

  const convertedAmount = useMemo(() => {
    const nAmount = Number(receivedAmount);
    const nRate = Number(exchangeRate);
    if (!Number.isFinite(nAmount) || !Number.isFinite(nRate) || nAmount <= 0 || nRate <= 0) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [receivedAmount, exchangeRate]);

  // Validate form fields.
  const validate = () => {
    let newErrors = {};
    if (!shop) newErrors.shop = t("sale.shopRequired") || "Shop is required";
    if (!customerSelected)
      newErrors.customer = t("sale.customerRequired") || "Customer is required";
    if (!saleDate)
      newErrors.saleDate =
        t("sale.saleDateRequired") || "Sale date is required";
    if (!saleCurrency)
      newErrors.currency = t("sale.currencyRequired") || "Currency is required";
    if (items.length === 0) {
      newErrors.items =
        t("sale.atLeastOneItemRequired") ||
        "At least one sale item is required";
    } else {
      items.forEach((item, idx) => {
        if (!item.product)
          newErrors[`item_${idx}_product`] =
            t("sale.productRequired") || "Product is required";
        if (!item.unit)
          newErrors[`item_${idx}_unit`] =
            t("sale.unitRequired") || "Unit is required";
        if (!item.quantity)
          newErrors[`item_${idx}_quantity`] =
            t("sale.quantityRequired") || "Quantity is required";
        else if (parseFloat(item.quantity) > parseFloat(item.available || "0"))
          newErrors[`item_${idx}_quantity`] =
            t("sale.quantityExceedsAvailable") ||
            "Quantity exceeds available stock";
        if (!item.sale_price)
          newErrors[`item_${idx}_salePrice`] =
            t("sale.salePriceRequired") || "Sale price is required";
      });
    }
    if (!receivedValid) {
      newErrors.receivedAmount =
        t("sale.receivedExceedsTotal") ||
        "Received amount should not exceed total amount";
    }
    if (needsPaymentAccount && (!selectedAccountId || !exchangeRate)) {
      newErrors.account =
        t("sale.accountRequired") ||
        "Select an account and exchange rate for the received amount";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const payload = {
      shop,
      customer: customerSelected,
      general_customer_name: isGeneralCustomerSelected ? (generalCustomerName.trim() || undefined) : undefined,
      general_customer_phone: isGeneralCustomerSelected ? (generalCustomerPhone.trim() || undefined) : undefined,
      amount: discountValue,
      received_amount: receivedAmount,
      sale_date: saleDate,
      currency: saleCurrency,
      account: needsPaymentAccount ? selectedAccountId : null,
      exchange_rate: needsPaymentAccount ? exchangeRate : "",
      payment_date: saleDate ? `${saleDate}T00:00:00` : new Date().toISOString(),
      payment_notes: paymentNotes,
      notes: notes.trim() || undefined,
      items,
    };
    handleAdd(payload);
  };

  useEffect(() => {
    if (resetForm) {
      setShop(hasSingleShop ? String(shopsList[0].id) : "");
      setCustomerSelected("");
      setGeneralCustomerName("");
      setGeneralCustomerPhone("");
      setDiscount("");
      setSelectedEmployeeId("");
      setReceivedAmount("");
      setSelectedAccountId("");
      setExchangeRate("");
      setPaymentNotes("");
      setNotes("");
      setPriceType("retail");
      setSaleDate("");
      setSaleCurrency("");
      setItems([]);
      setErrors({});
      setFormError("");
      onClose();
    }
  }, [resetForm, onClose, hasSingleShop, shopsList]);

  const getEmployeeName = (employee) => {
    if (!employee) return "";
    if (employee.first_name && employee.last_name) {
      return `${employee.first_name} ${employee.last_name}`;
    }
    return employee.username || employee.name || "";
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{t("sale.addSale")}</DialogTitle>
      <DialogContent>
        {formError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {formError}
          </Typography>
        )}
        <Box
          component="form"
          noValidate
          sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}
        >
          {/* Sale-level fields arranged in two columns */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.selectShop")}
                fullWidth
                required
                value={shop}
                onChange={(e) => setShop(e.target.value)}
                disabled={shopLoading || hasSingleShop}
                error={Boolean(errors.shop)}
                helperText={errors.shop}
              >
                {shopsList.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={customerOptions}
                getOptionLabel={(option) => option.name || ""}
                getOptionKey={(option) => option.id}
                value={customerOptions.find((c) => String(c.id) === customerSelected) || null}
                onChange={(event, newValue) => setCustomerSelected(newValue ? String(newValue.id) : "")}
                loading={customerLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("sale.selectCustomer")}
                    required
                    error={Boolean(errors.customer)}
                    helperText={errors.customer}
                  />
                )}
              />
            </Grid>
            {selectedCustomerObject && !isGeneralCustomerSelected && (
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t("customer.amountOwed", "Amount owed")}
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedCustomerBalanceText}
                  </Typography>
                </Box>
              </Grid>
            )}
            {isGeneralCustomerSelected && (
              <>
                <Grid item xs={6}>
                  <TextField label="General Customer Name" fullWidth value={generalCustomerName} onChange={(e) => setGeneralCustomerName(e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                  <TextField label="General Customer Phone Number" fullWidth value={generalCustomerPhone} onChange={(e) => setGeneralCustomerPhone(e.target.value)} />
                </Grid>
              </>
            )}
            <Grid item xs={6}>
              <TextField
                label={t("sale.saleDate")}
                type="date"
                fullWidth
                required
                value={saleDate}
                onChange={(e) => setSaleDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.saleDate)}
                helperText={errors.saleDate}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.currency")}
                fullWidth
                required
                value={saleCurrency}
                onChange={(e) => setSaleCurrency(e.target.value)}
                disabled={currencyLoading}
                error={Boolean(errors.currency)}
                helperText={errors.currency}
                SelectProps={{
                  renderValue: (value) => {
                    const currency =
                      currenciesList.find((curr) => String(curr.id) === String(value)) ||
                      allCurrenciesList.find((curr) => String(curr.id) === String(value));
                    return getCurrencyLabel(currency);
                  },
                }}
              >
                {currenciesList.map((curr) => (
                  <MenuItem key={curr.id} value={String(curr.id)}>
                    {getCurrencyLabel(curr)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label={t("Discount-Amount")}
                type="number"
                fullWidth
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                error={Boolean(errors.discount)}
                helperText={errors.discount}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                label={t("sale.selectEmployee", "Select Employee")}
                fullWidth
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                disabled={employeeLoading}
              >
                <MenuItem value="">
                  {t("sale.selectEmployee", "Select Employee")}
                </MenuItem>
                {employeesList.map((employee) => (
                  <MenuItem key={employee.id} value={String(employee.id)}>
                    {getEmployeeName(employee)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("sale.note")}
                fullWidth
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("sale.notePlaceholder")}
              />
            </Grid>
            {/* Price Type Selector */}
            <Grid item xs={12}>
              <TextField
                select
                label={t("sale.priceType", "Price Type")}
                fullWidth
                value={priceType}
                onChange={(e) => handlePriceTypeChange(e.target.value)}
                helperText={t(
                  "sale.priceTypeHelper",
                  "Choosing a price type auto-fills the sale price for every item you add"
                )}
              >
                <MenuItem value="retail">
                  🛒 {t("purchase.retailPrice", "Retail Price")}
                </MenuItem>
                <MenuItem value="wholesale">
                  🏷️ {t("purchase.wholesalePrice", "Wholesale Price")}
                </MenuItem>
              </TextField>
            </Grid>
          </Grid>
          {/* Sale Items Section */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6">{t("sale.saleItems")}</Typography>
            {errors.items && (
              <Typography color="error" variant="body2">
                {errors.items}
              </Typography>
            )}
            {items.map((item, index) => {
              const selectedIds = items.map((i) => i.product);
              const filteredOptions = Array.isArray(uniqueProducts) 
                ? uniqueProducts.filter(
                    (prod) =>
                      String(prod.id) === item.product ||
                      !selectedIds.includes(String(prod.id))
                  )
                : [];
              return (
                <Box
                  key={index}
                  sx={{
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    p: 1,
                    mb: 1,
                  }}
                >
                  {/* Row 1: Autocomplete product select and Remove icon */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Autocomplete
                      options={filteredOptions}
                      getOptionLabel={(option) => option.name}
                      getOptionKey={(option) => option.id}
                      value={
                        filteredOptions.find(
                          (p) => String(p.id) === item.product
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        handleProductSelect(index, newValue ? newValue : null)
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("sale.selectProduct")}
                          required
                          error={Boolean(errors[`item_${index}_product`])}
                          helperText={errors[`item_${index}_product`]}
                        />
                      )}
                      sx={{ flex: 2 }}
                    />
                    <IconButton onClick={() => removeItem(index)} color="error">
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  </Box>
                  {/* Row 2: All sale item fields in one row */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      label={t("sale.availableQuantity")}
                      value={item.available || ""}
                      sx={{ flex: 1 }}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      label={t("sale.quantity")}
                      type="number"
                      required
                      value={item.quantity}
                      onChange={(e) =>
                        handleItemChange(index, "quantity", e.target.value)
                      }
                      sx={{ flex: 1 }}
                      error={Boolean(errors[`item_${index}_quantity`])}
                      helperText={errors[`item_${index}_quantity`]}
                    />
                    <TextField
                      select
                      label={t("sale.selectUnit")}
                      required
                      value={item.unit}
                      onChange={(e) =>
                        handleItemChange(index, "unit", e.target.value)
                      }
                      sx={{ flex: 1 }}
                      disabled={unitLoading}
                      error={Boolean(errors[`item_${index}_unit`])}
                      helperText={errors[`item_${index}_unit`]}
                    >
                      {unitsList.map((unit) => (
                        <MenuItem key={unit.id} value={String(unit.id)}>
                          {unit.title}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label={t("purchase.buyPrice", "Buy Price")}
                      value={item.buy_price || ""}
                      onChange={(e) =>
                        handleItemChange(index, "buy_price", e.target.value)
                      }
                      sx={{ flex: 1 }}
                      type="number"
                    />
                    <TextField
                      label={`${t("sale.salePrice")} ${selectedCurrencyTitle ? `(${selectedCurrencyTitle})` : ""}`}
                      type="number"
                      required
                      value={item.sale_price}
                      onChange={(e) =>
                        handleItemChange(index, "sale_price", e.target.value)
                      }
                      sx={{ flex: 1 }}
                      error={Boolean(errors[`item_${index}_salePrice`])}
                      helperText={errors[`item_${index}_salePrice`]}
                    />
                  </Box>
                </Box>
              );
            })}
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={addItem}
              sx={{ mt: 1 }}
            >
              {t("sale.addItem")}
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1">
              {t("sale.subtotal")}: {totalAmount.toFixed(2)}
            </Typography>
            {discountValue > 0 && (
              <Typography variant="body1">
                {t("sale.discountAmount")}: -{discountValue.toFixed(2)}
              </Typography>
            )}
            <Typography variant="h6">
              {t("sale.totalAmount")}: {finalTotal.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("sale.receivedAmount", "Received Amount")}
                  type="number"
                  fullWidth
                  value={receivedAmount}
                  onChange={(e) => setReceivedAmount(e.target.value)}
                  error={Boolean(errors.receivedAmount)}
                  helperText={errors.receivedAmount}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label={t("sale.account", "Account")}
                  fullWidth
                  required={needsPaymentAccount}
                  value={selectedAccountId}
                  onChange={(e) => setSelectedAccountId(e.target.value)}
                  disabled={!shop || accountLoading}
                  error={Boolean(errors.account)}
                  helperText={errors.account}
                >
                  {accountLoading ? (
                    <MenuItem value="">
                      <CircularProgress size={20} />
                    </MenuItem>
                  ) : accounts.length === 0 ? (
                    <MenuItem value="" disabled>
                      {t("sale.noAccount", "No accounts found")}
                    </MenuItem>
                  ) : (
                    accounts.map((acc) => (
                      <MenuItem key={acc.id} value={String(acc.id)}>
                        {acc.name} ({acc.currency_title || "-"})
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("sale.exchangeRate", "Exchange Rate")}
                  type="number"
                  fullWidth
                  required={needsPaymentAccount}
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  inputProps={{ min: 0, step: "0.000001" }}
                  disabled={!needsPaymentAccount}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t("sale.convertedAmount", "Converted Amount")}
                  fullWidth
                  value={convertedAmount}
                  disabled
                />
              </Grid>
              {!exchangeRatePreview && needsPaymentAccount && selectedAccountId && !exchangeRate && (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    {t("sale.missingRate", "No exchange rate found for selected account currency. You can enter it manually.")}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  label={t("sale.paymentNotes", "Payment Notes")}
                  fullWidth
                  multiline
                  rows={2}
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("sale.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("sale.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaleAdd;
